import { Language } from "../../../utils/dictionary";
import type { ChatChannelType } from "../config/integrations.config";
import type { LucideIcon } from "lucide-react";

export interface ChannelWizardPermission {
  id: string;
  label: Record<Language, string>;
}

export interface ChannelWizardChecklistItem {
  id: string;
  label: Record<Language, string>;
}

export interface ChannelWizardStep {
  id: string;
  label: Record<Language, string>;
}

export interface ChannelWizardFeature {
  icon: LucideIcon;
  label: Record<Language, string>;
}

export interface ChannelWizardConfig {
  channel: ChatChannelType;
  title: Record<Language, string>;
  description: Record<Language, string>;
  logoSrc: string;
  fluxiLogoSrc: string;
  accentColor: string;
  connectButtonLabel: Record<Language, string>;
  permissions: ChannelWizardPermission[];
  checklist: ChannelWizardChecklistItem[];
  connectionSteps: ChannelWizardStep[];
  features: ChannelWizardFeature[];
}

interface ChannelIntegrationWizardProps {
  config: ChannelWizardConfig;
  language: Language;
  loading?: boolean;
  sdkReady?: boolean;
  error?: string | null;
  onClose: () => void;
  onConnect: () => void;
}

const ChannelIntegrationWizard = ({
  config,
  language,
  loading = false,
  sdkReady = true,
  error = null,
  onClose,
  onConnect,
}: ChannelIntegrationWizardProps) => {
  const isLoading = loading;
  const disabled = !sdkReady || isLoading;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="relative flex w-full max-w-[850px] flex-col overflow-hidden rounded-xl bg-white shadow-xl md:flex-row">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 p-2 text-gray-400 hover:text-gray-600 md:hidden"
          type="button"
        >
          ×
        </button>

        <div className="flex flex-1 flex-col p-6 md:p-8">
          <div className="mb-5 flex items-center gap-3">
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full"
              style={{ backgroundColor: `${config.accentColor}20` }}
            >
              <img src={config.logoSrc} alt="" className="h-7 w-7 object-contain" />
            </div>
            <h2 className="text-2xl font-bold leading-tight text-gray-900">{config.title[language]}</h2>
          </div>

          <p className="mb-6 text-sm leading-relaxed text-gray-600">{config.description[language]}</p>

          <div className="mb-6 space-y-3">
            {config.features.map((feature) => {
              const Icon = feature.icon;
              return (
                <div key={feature.label[language]} className="flex items-start gap-3">
                  <Icon className="mt-0.5 h-5 w-5 shrink-0" style={{ color: config.accentColor }} />
                  <span className="text-sm text-gray-700">{feature.label[language]}</span>
                </div>
              );
            })}
          </div>

          <div className="mb-6 rounded-xl border border-gray-100 bg-gray-50 p-4">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Antes de conectar</p>
            <ul className="space-y-2">
              {config.checklist.map((item) => (
                <li key={item.id} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400" />
                  {item.label[language]}
                </li>
              ))}
            </ul>
          </div>

          <div className="mb-6">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Permisos requeridos</p>
            <div className="flex flex-wrap gap-2">
              {config.permissions.map((permission) => (
                <span
                  key={permission.id}
                  className="rounded-full bg-white px-3 py-1 text-xs font-medium text-gray-600 ring-1 ring-gray-200"
                >
                  {permission.label[language]}
                </span>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Pasos de conexión</p>
            <ol className="space-y-2">
              {config.connectionSteps.map((step, index) => (
                <li key={step.id} className="flex items-center gap-3 text-sm text-gray-700">
                  <span
                    className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
                    style={{ backgroundColor: config.accentColor }}
                  >
                    {index + 1}
                  </span>
                  {step.label[language]}
                </li>
              ))}
            </ol>
          </div>

          {error ? (
            <div className="mb-4 w-full rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          ) : null}

          <div className="mt-auto border-t border-gray-100 pt-4">
            <div className="flex gap-3">
              <button
                type="button"
                className="flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                style={{ backgroundColor: config.accentColor }}
                onClick={onConnect}
                disabled={disabled}
              >
                {isLoading ? (
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                ) : (
                  <>
                    <img src={config.logoSrc} alt="" className="h-5 w-5 object-contain" />
                    {config.connectButtonLabel[language]}
                  </>
                )}
              </button>
              <button
                type="button"
                className="flex-1 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                onClick={onClose}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>

        <div className="relative hidden w-[200px] shrink-0 items-center justify-center border-l border-gray-100 bg-gradient-to-b from-gray-50 to-white px-4 py-6 md:flex">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 z-10 p-2 text-gray-400 transition-colors hover:text-gray-600"
            type="button"
          >
            ×
          </button>
          <div className="flex h-full w-full flex-col items-center justify-center gap-8">
            <div className="relative z-10 flex h-24 w-24 items-center justify-center rounded-[24px] border border-blue-100 bg-white p-5 shadow-[0_4px_20px_rgba(67,104,229,0.15)]">
              <img src={config.fluxiLogoSrc} alt="Fluxi" className="h-auto w-full object-contain" />
            </div>
            <div className="flex flex-col items-center gap-1" style={{ color: config.accentColor }}>
              {[0, 200, 400].map((delay) => (
                <span
                  key={delay}
                  className="text-2xl leading-none"
                  style={{ animation: `channelWizardFade 1.5s infinite ${delay}ms` }}
                >
                  ↓
                </span>
              ))}
            </div>
            <div className="relative z-10 flex h-24 w-24 items-center justify-center rounded-[24px] border border-gray-200 bg-white p-5 shadow-sm">
              <img src={config.logoSrc} alt="" className="h-12 w-12 object-contain" />
            </div>
            <style>{`
              @keyframes channelWizardFade {
                0%, 100% { opacity: 0.3; transform: translateY(0); }
                50% { opacity: 1; transform: translateY(3px); }
              }
            `}</style>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChannelIntegrationWizard;
export type { ChannelIntegrationWizardProps };
